import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function FlangerLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Flanger in Music Production: What It Is and How It Works",
      description:
        "Learn what a flanger does, how it creates sweeping comb-filter motion, how it differs from chorus and phaser, and where flangers are used in music production.",
      author: { "@type": "Organization", name: "Parish Audio" },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/flanger",
      articleSection: "Audio Education",
      keywords: [
        "flanger",
        "what does a flanger do",
        "flanger effect",
        "flanger vs chorus",
        "flanger vs phaser",
        "guitar flanger",
        "audio modulation",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A flanger mixes a signal with a very short, modulated delay, creating moving comb-filter peaks and notches that sound jet-like, metallic, or sweeping.",
      icon: <SyncRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Flanging adds obvious motion, texture, and dramatic character, ranging from subtle shimmer to extreme whooshing effects.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Guitar, drums, vocals, synths, bass, transitions, special effects, and classic rock or electronic production.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const flangerTypes = [
    {
      name: "Classic Tape-Style Flanger",
      description:
        "Inspired by the original reel-to-reel tape technique, often sounding smooth, rich, and dramatic with a very organic sweep.",
    },
    {
      name: "Pedal Flanger",
      description:
        "A stompbox-style version commonly used on guitar, with a familiar sweeping jet effect and hands-on control.",
    },
    {
      name: "Stereo Flanger",
      description:
        "Uses left and right movement to create a wider, more immersive modulation effect.",
    },
    {
      name: "Feedback Flanger",
      description:
        "Feeds some output back into the effect for stronger resonance and more intense comb-filter character.",
    },
    {
      name: "Through-Zero Flanger",
      description:
        "A deeper, more dramatic style associated with classic tape flanging where the delayed signal crosses the dry signal timing point.",
    },
    {
      name: "Digital Experimental Flanger",
      description:
        "Can push flanging into sharper, cleaner, or more extreme modulation territory than many vintage designs.",
    },
  ];

  const flangerConcepts = [
    {
      name: "Short Modulated Delay",
      description:
        "A flanger works with a very short delay time that changes continuously over time, rather than a longer audible echo.",
    },
    {
      name: "Comb Filtering",
      description:
        "Mixing the delayed signal with the dry signal creates a pattern of peaks and notches across the frequency spectrum.",
    },
    {
      name: "Moving Sweep",
      description:
        "As the delay time changes, the peaks and notches move, creating the signature sweeping or jet-like sound.",
    },
    {
      name: "Feedback Intensity",
      description:
        "Feedback emphasizes the effect and makes the sweep more resonant, metallic, and dramatic.",
    },
  ];

  const controls = [
    {
      name: "Rate / Speed",
      description:
        "Controls how fast the flanger sweep moves. Slow settings are broad and dramatic, while faster settings feel more animated and obvious.",
    },
    {
      name: "Depth",
      description:
        "Controls how wide the delay modulation moves, affecting how pronounced the sweep feels.",
    },
    {
      name: "Feedback / Regen",
      description:
        "Feeds part of the output back into the effect, increasing resonance and making the comb-filter motion more intense.",
    },
    {
      name: "Manual / Delay Time",
      description:
        "Sets the general center point of the very short delay range, which changes the character of the sweep.",
    },
    {
      name: "Mix",
      description:
        "Blends the dry and effected signals, making the result subtle or very obvious.",
    },
    {
      name: "Stereo Spread",
      description:
        "On stereo flangers, controls how the movement is distributed across left and right channels.",
    },
  ];

  const commonUses = [
    "Creating classic jet-like guitar sweeps",
    "Adding motion and drama to sustained synths or pads",
    "Making transitions and special effects feel larger and more animated",
    "Adding metallic movement to drum buses or percussion loops",
    "Creating psychedelic or vintage production textures",
    "Giving vocals or background elements a stylized moving tone",
  ];

  const bestPractices = [
    "Use feedback carefully, because a little can go a long way with a flanger.",
    "Check the effect in context, since heavy flanging can quickly dominate a mix.",
    "Lower mix settings often work well when you want motion without overwhelming the source.",
    "Slow sweeps can sound huge and dramatic, while fast sweeps can become more obviously effect-like.",
    "Compare it with chorus or phaser if you want movement but are unsure which modulation texture fits best.",
  ];

  const relatedLinks = [
    {
      title: "Phaser",
      to: "/learning/phaser",
      description:
        "Learn how phasers create movement through phase-shifting rather than the short delay-based comb filtering of a flanger.",
    },
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "Explore how chorus aims more for width and shimmer, while flanger usually sounds more obvious and sweeping.",
    },
    {
      title: "Delay",
      to: "/learning/delay",
      description:
        "See how delay creates repeats over time, while flanging uses a much shorter modulated delay for spectral motion.",
    },
    {
      title: "Reverb",
      to: "/learning/reverb",
      description:
        "Understand how reverb adds space, while flanger adds motion and comb-filter texture.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Flanger | Parish Audio Learning"
        description="Learn what a flanger does, how it creates sweeping comb-filter motion, how it differs from chorus and phaser, and where flangers are used in music production."
        canonical="https://parishaudio.com/learning/flanger"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Flanger in Music Production: What It Is and How It Works
              </Typography>

              <Typography sx={{ color: style.color3, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.85, maxWidth: 950 }}>
                A flanger is a modulation effect that creates a moving comb-filter sound by mixing
                the original signal with a very short delayed version of itself. As that tiny delay
                changes over time, peaks and notches sweep across the frequency range, producing the
                iconic jet-like, metallic, or swirling sound associated with flanging.
              </Typography>

              <Typography sx={{ color: style.color3, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.85, mt: 2, maxWidth: 950 }}>
                Flangers can be subtle, but they are often used for more obvious motion than chorus
                or phaser. They have long been part of rock, electronic music, sound design, and
                experimental production because they can transform an otherwise plain sound into
                something much more animated and dramatic.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.97rem" }}>{item.text}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                What is a flanger?
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                A flanger is a time-based modulation effect built from an extremely short delay.
                Unlike a standard delay, the delay time is so short that you do not hear distinct
                repeats. Instead, the delayed signal combines with the dry signal and creates a
                pattern of cancellations and boosts across the spectrum.
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                As the delay time changes, that pattern moves. This motion is what creates the
                sweeping effect that people associate with flanging. Depending on the settings, the
                result can be silky and spacious or intense and almost mechanical.
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                Flangers are often described as more dramatic than chorus and more obviously
                comb-filtered than phasers. That strong personality is part of what makes them so
                memorable.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How a flanger works
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {flangerConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>{item.name}</Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item.description}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                The classic flanger sound often comes from using an LFO to move the delay time
                smoothly up and down. When feedback is added, the notches become more pronounced,
                which makes the effect more dramatic and recognizable.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common types of flangers
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {flangerTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>{item.name}</Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item.description}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Flanger vs chorus and phaser
              </Typography>
              <Paper sx={{ p: 2, borderRadius: 3, backgroundColor: darkPanel, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Related modulation effects, different feel
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Chorus usually aims for width, richness, and a slightly doubled or detuned feel.
                    Phaser creates moving notches through phase shifting. Flanger is more strongly
                    associated with comb-filter sweeps from a very short modulated delay.
                  </Typography>
                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In listening terms, flangers often sound more metallic, more jet-like, and more
                    obviously dramatic than chorus. They may also sound more intense and comb-like
                    than many phasers.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common flanger controls
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>{item.name}</Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item.description}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How flangers are used in production
              </Typography>
              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                A flanger can work as a spotlight effect or as a layer of motion beneath a source.
                It is especially effective when you want a sound to feel less static and more alive,
                but with a clearly noticeable modulation personality.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Best practices for using a flanger
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item}</Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Related learning topics
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>{item.title}</Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>{item.description}</Typography>
                      </Box>
                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Final thoughts
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                A flanger is one of the most recognizable modulation effects in music production.
                Its moving comb-filter sound can be subtle, dramatic, vintage, futuristic, or
                intensely psychedelic depending on how it is set.
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                When used carefully, a flanger can add motion and excitement without completely
                taking over the source. When pushed hard, it can become a major part of the sound
                itself, which is exactly why so many producers and players love it.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}